import React from 'react';
import { Layout } from '../components/Layout';
import { SEO, pageSEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/Card';
import { Target, Users, TrendingUp, CheckCircle2, Calendar, MessageCircle, GraduationCap, Award, Briefcase, Eye, Recycle, ArrowRight, Sparkles, BarChart3, Heart } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';
export function ImpactPage() {
  return <Layout data-id="element-1583">
      <SEO {...pageSEO.impact} data-id="element-1584" />
      {/* Header */}
      <div className="bg-[#2C3E50] text-white py-16" data-id="element-1585">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1586">
          <span className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1 text-sm font-medium text-blue-200 mb-4" data-id="element-1587">
            Une association naissante, des ambitions concrètes
          </span>
          <h1 className="text-4xl font-bold mb-4" data-id="element-1588">Notre Vision d'Impact</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-1589">
            L'ADCC construit son impact pas à pas, avec transparence et rigueur.
            Découvrez notre point de départ, nos objectifs 2026 et comment nous
            mesurerons notre réussite.
          </p>
        </div>
      </div>

      {/* Notre Point de Départ - Le Forum Fondateur */}
      <section className="py-20 bg-white" data-id="element-1590">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1591">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1592">
            <div data-id="element-1593">
              <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1594">
                Notre Point de Départ
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6" data-id="element-1595">
                Le Forum Fondateur
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-1596">
                Nos premiers pas ont été marqués par un{' '}
                <strong data-id="element-1597">forum d'état des lieux</strong>, qui a réuni une dizaine
                de personnels de maison de différents domaines pour
                cartographier leurs besoins et aspirations.
              </p>

              <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-r-lg mb-6" data-id="element-1598">
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-1599">
                  <MessageCircle className="w-5 h-5 text-amber-600" data-id="element-1600" />
                  Ce que nous avons appris
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-1601">
                  <li className="flex items-start gap-2" data-id="element-1602">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1603" />
                    <span data-id="element-1604">Un besoin réel en compétences professionnelles</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1605">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1606" />
                    <span data-id="element-1607">
                      Une demande forte de reconnaissance et de dignité
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1608">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1609" />
                    <span data-id="element-1610">
                      L'importance d'un accompagnement vers l'emploi structuré
                    </span>
                  </li>
                </ul>
              </div>

              <p className="text-gray-600" data-id="element-1611">
                Ce forum a posé les bases de notre approche :{' '}
                <strong data-id="element-1612">écouter d'abord, agir ensuite</strong>. Chaque action de
                l'ADCC est construite à partir des besoins réels exprimés par
                les premiers concernés.
              </p>
            </div>

            <div className="relative" data-id="element-1613">
              <div className="rounded-2xl overflow-hidden shadow-xl" data-id="element-1614">
                <img src="https://cdn.magicpatterns.com/uploads/exRoSvgKroC4rrK6KpbESt/WhatsApp_Image_2026-01-29_at_17.21.09.jpg" alt="Forum fondateur ADCC - Réunion avec les personnels de maison" className="w-full h-auto object-cover" data-id="element-1615" />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white p-4 rounded-lg shadow-lg border border-gray-100 hidden md:block" data-id="element-1616">
                <div className="flex items-center gap-3" data-id="element-1617">
                  <div className="w-12 h-12 bg-[#1E88E5] rounded-full flex items-center justify-center" data-id="element-1618">
                    <Users className="w-6 h-6 text-white" data-id="element-1619" />
                  </div>
                  <div data-id="element-1620">
                    <p className="font-bold text-gray-900" data-id="element-1621">Forum Fondateur</p>
                    <p className="text-sm text-gray-600" data-id="element-1622">2024 • Bénin</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nos Objectifs 2026 */}
      <section className="py-20 bg-gray-50" data-id="element-1623">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1624">
          <div className="text-center mb-12" data-id="element-1625">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1626">
              Cap sur 2026
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1627">
              Nos Objectifs Chiffrés
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1628">
              Des ambitions réalistes et mesurables pour notre première année
              d'activité pleine.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-1629">
            <Card className="text-center border-t-4 border-t-[#1E88E5] bg-white" data-id="element-1630">
              <CardContent className="pt-8 pb-6" data-id="element-1631">
                <div className="inline-flex items-center justify-center p-4 bg-blue-50 rounded-full mb-4" data-id="element-1632">
                  <GraduationCap className="w-8 h-8 text-[#1E88E5]" data-id="element-1633" />
                </div>
                <p className="text-5xl font-bold text-[#1E88E5] mb-2" data-id="element-1634">100</p>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1635">
                  Personnes à accompagner
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-1636">
                  Via nos ateliers de Mars 2026 et sessions complémentaires
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-t-4 border-t-green-500 bg-white" data-id="element-1637">
              <CardContent className="pt-8 pb-6" data-id="element-1638">
                <div className="inline-flex items-center justify-center p-4 bg-green-50 rounded-full mb-4" data-id="element-1639">
                  <Briefcase className="w-8 h-8 text-green-600" data-id="element-1640" />
                </div>
                <p className="text-5xl font-bold text-green-600 mb-2" data-id="element-1641">60%</p>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1642">
                  Taux de placement visé
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-1643">
                  Des bénéficiaires accompagnés insérés professionnellement
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-t-4 border-t-purple-500 bg-white" data-id="element-1644">
              <CardContent className="pt-8 pb-6" data-id="element-1645">
                <div className="inline-flex items-center justify-center p-4 bg-purple-50 rounded-full mb-4" data-id="element-1646">
                  <Heart className="w-8 h-8 text-purple-600" data-id="element-1647" />
                </div>
                <p className="text-5xl font-bold text-purple-600 mb-2" data-id="element-1648">100%</p>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1649">
                  D'insertion éthique
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-1650">
                  Transparents et respectueux des droits
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100" data-id="element-1651">
            <div className="flex flex-col md:flex-row items-center gap-6" data-id="element-1652">
              <div className="flex-shrink-0" data-id="element-1653">
                <div className="w-16 h-16 bg-[#1E88E5]/10 rounded-full flex items-center justify-center" data-id="element-1654">
                  <Target className="w-8 h-8 text-[#1E88E5]" data-id="element-1655" />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left" data-id="element-1656">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1657">
                  Pourquoi ces objectifs ?
                </h3>
                <p className="text-gray-600" data-id="element-1658">
                  Ces chiffres sont ambitieux mais réalistes pour une première
                  année. Nous préférons des objectifs atteignables que nous
                  pourrons dépasser, plutôt que des promesses impossibles à
                  tenir. La crédibilité se construit dans la durée.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notre Théorie du Changement */}
      <section className="py-20 bg-white" data-id="element-1659">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1660">
          <div className="text-center mb-12" data-id="element-1661">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1662">
              Notre Approche
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1663">
              La Chaîne de Valeur ADCC
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1664">
              Une approche complète, de la montée en compétences à l'emploi
              durable, pour un impact réel et mesurable.
            </p>
          </div>

          <div className="relative" data-id="element-1665">
            {/* Connection line - desktop only */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-green-200 to-purple-200 transform -translate-y-1/2 z-0" data-id="element-1666"></div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 relative z-10" data-id="element-1667">
              {[{
              icon: GraduationCap,
              title: 'Développer',
              desc: 'Compétences techniques et humaines',
              color: 'bg-blue-50',
              iconColor: 'text-blue-600'
            }, {
              icon: Award,
              title: 'Tester',
              desc: 'Validation et professionnalisation',
              color: 'bg-green-50',
              iconColor: 'text-green-600'
            }, {
              icon: Briefcase,
              title: 'Insérer',
              desc: 'Mise en relation avec employeurs',
              color: 'bg-purple-50',
              iconColor: 'text-purple-600'
            }, {
              icon: Eye,
              title: 'Suivre',
              desc: 'Accompagnement post-placement',
              color: 'bg-amber-50',
              iconColor: 'text-amber-600'
            }, {
              icon: Recycle,
              title: 'Recycler',
              desc: 'Mise à jour des compétences',
              color: 'bg-teal-50',
              iconColor: 'text-teal-600'
            }].map((step, index) => <div key={index} className="flex flex-col items-center" data-id="element-1668">
                  <div className={`w-20 h-20 ${step.color} rounded-full flex items-center justify-center mb-4 shadow-sm border-4 border-white`} data-id="element-1669">
                    <step.icon className={`w-10 h-10 ${step.iconColor}`} data-id="element-1670" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-1671">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600 text-center" data-id="element-1672">
                    {step.desc}
                  </p>
                </div>)}
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-r from-[#1E88E5] to-[#1976D2] rounded-2xl p-8 text-white text-center" data-id="element-1673">
            <Sparkles className="w-10 h-10 mx-auto mb-4 text-yellow-300" data-id="element-1674" />
            <p className="text-2xl font-bold mb-2" data-id="element-1675">
              "Notre engagement : transformer les compétences en opportunités
              d'emploi durables."
            </p>
          </div>
        </div>
      </section>

      {/* Comment nous mesurerons notre impact */}
      <section className="py-20 bg-gray-50" data-id="element-1676">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1677">
          <div className="text-center mb-12" data-id="element-1678">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1679">
              Transparence & Rigueur
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1680">
              Comment nous mesurerons notre impact
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1681">
              Des indicateurs clairs pour évaluer notre réussite et rendre
              compte à nos partenaires et bénéficiaires.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1682">
            {[{
            icon: Users,
            title: 'Nombre de bénéficiaires accompagnés',
            desc: 'Comptabilisation des participants ayant complété nos programmes de formation',
            target: 'Objectif 2026 : 100 personnes'
          }, {
            icon: Briefcase,
            title: "Taux d'insertion professionnelle",
            desc: 'Pourcentage de bénéficiaires de nos ateliers ayant trouvé un emploi dans les 6 mois',
            target: 'Objectif 2026 : 60%'
          }, {
            icon: TrendingUp,
            title: 'Durabilité des placements',
            desc: 'Suivi à 6 et 12 mois pour mesurer la stabilité des emplois obtenus',
            target: 'Suivi systématique prévu'
          }, {
            icon: BarChart3,
            title: 'Satisfaction des bénéficiaires',
            desc: 'Enquêtes régulières auprès des personnes formées et placées',
            target: 'Questionnaires post-formation et post-placement'
          }].map((metric, index) => <Card key={index} className="bg-white" data-id="element-1683">
                <CardContent className="p-6" data-id="element-1684">
                  <div className="flex items-start gap-4" data-id="element-1685">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-1686">
                      <metric.icon className="w-6 h-6 text-[#1E88E5]" data-id="element-1687" />
                    </div>
                    <div className="flex-1" data-id="element-1688">
                      <h3 className="font-bold text-gray-900 mb-2" data-id="element-1689">
                        {metric.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-3" data-id="element-1690">
                        {metric.desc}
                      </p>
                      <span className="inline-block bg-blue-50 text-[#1E88E5] text-xs font-medium px-3 py-1 rounded-full" data-id="element-1691">
                        {metric.target}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>

          <div className="mt-12 bg-white rounded-xl p-8 border border-gray-100 shadow-sm" data-id="element-1692">
            <div className="flex flex-col md:flex-row items-center gap-6" data-id="element-1693">
              <div className="flex-shrink-0" data-id="element-1694">
                <Calendar className="w-12 h-12 text-[#1E88E5]" data-id="element-1695" />
              </div>
              <div className="flex-1 text-center md:text-left" data-id="element-1696">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1697">
                  Rendez-vous fin 2026
                </h3>
                <p className="text-gray-600" data-id="element-1698">
                  Nous publierons notre premier rapport d'impact fin 2026, avec
                  des données réelles et vérifiables. D'ici là, suivez notre
                  actualité sur notre blog et nos réseaux sociaux pour des mises
                  à jour régulières sur nos avancées.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}